(() => {
  if (customElements.get('repenic-builder')) return;
  const finishes = ['brass', 'steel', 'black', 'white'];
  const styles = ['rotary', 'toggle'];
  const labels = { brass: 'Brass', steel: 'Steel', black: 'Black', white: 'White', rotary: 'Rotary', toggle: 'Toggle', rocker: 'Rocker' };
  const tones = { brass: '#c8a665', steel: '#b6b6b3', black: '#242424', white: '#eeeeee' };
  const normalise = value => String(value).trim().toLowerCase();
  class RepenicBuilder extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.data = JSON.parse(this.querySelector('[data-rb-data]').textContent);
      this.gang = 1;
      this.finish = 'brass';
      this.controls = Array.from({ length: 4 }, () => ({ style: 'rotary', finish: 'brass' }));
      this.dialog = this.querySelector('dialog.rb-dialog');
      this.busy = false;
      this.clickHandler = event => this.handleClick(event);
      this.cancelHandler = event => { if (this.busy) event.preventDefault(); };
      this.closeHandler = () => { this.unlock(); this.querySelector('[data-rb-open]').focus(); };
      this.addEventListener('click', this.clickHandler);
      this.dialog.addEventListener('cancel', this.cancelHandler);
      this.dialog.addEventListener('close', this.closeHandler);
      this.render();
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.clickHandler);
      this.dialog?.removeEventListener('cancel', this.cancelHandler);
      this.dialog?.removeEventListener('close', this.closeHandler);
      if (this.dialog?.open) this.dialog.close();
      this.unlock();
      this.ready = false;
    }
    unlock() {
      if (this.previousOverflow !== undefined) {
        document.documentElement.style.overflow = this.previousOverflow;
        this.previousOverflow = undefined;
      }
    }
    handleClick(event) {
      const summary = event.target.closest('.rb-position > summary');

      if (summary && this.contains(summary)) {
        event.preventDefault();

        if (this.busy) return;

        const currentPosition = summary.parentElement;
        const shouldOpen = !currentPosition.open;

        if (shouldOpen) {
          this.querySelectorAll('.rb-position[open]').forEach(position => {
            if (position !== currentPosition) position.open = false;
          });
        }

        currentPosition.open = shouldOpen;

        if (shouldOpen && window.matchMedia('(max-width: 749px)').matches) {
          const scroll = currentPosition.closest('.rb-scroll');

          if (scroll) {
            requestAnimationFrame(() => {
              if (!currentPosition.isConnected || !currentPosition.open) return;

              const top =
                scroll.scrollTop +
                summary.getBoundingClientRect().top -
                scroll.getBoundingClientRect().top -
                scroll.clientTop;

              scroll.scrollTo({
                top: Math.max(0, top),
                behavior: 'instant'
              });
            });
          }
        }

        return;
      }

      const button = event.target.closest('button');
      if (!button || !this.contains(button) || this.busy) return;
      if (button.hasAttribute('data-rb-open')) {
        this.previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        this.dialog.showModal();
        return;
      }
      if (button.hasAttribute('data-rb-close')) { this.dialog.close(); return; }
      if (button.hasAttribute('data-rb-add')) { this.addToCart(); return; }
      if (button.hasAttribute('data-rb-quantity-change')) {
        const input = this.querySelector('[data-rb-quantity]');
        const currentValue = Number(input.value);
        const quantity = Number.isSafeInteger(currentValue) && currentValue >= 1
          ? currentValue
          : 1;

        input.value = String(
          Math.max(1, quantity + Number(button.dataset.rbQuantityChange))
        );

        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      if (!button.dataset.choice) return;
      const { choice, value, index } = button.dataset;
      if (choice === 'gang') this.gang = Number(value);
      else if (choice === 'plate') this.finish = value;
      else this.controls[Number(index)][choice] = value;
      this.render();
      [...this.querySelectorAll('[data-choice]')].find(b => b.dataset.choice === choice && b.dataset.value === value && b.dataset.index === index)?.focus({ preventScroll: true });
    }
    choices(values, selected, choice, index) {
      const escape = value => String(value).replace(/[&<>"']/g, character => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      }[character]));
      const isFinish = choice === 'plate' || choice === 'finish';
      const control = choice === 'finish' ? this.controls[index] : null;
      const product = choice === 'plate'
        ? this.data.products.plate
        : control ? this.data.products[control.style] : null;
      const finishIndex = product?.options.findIndex(name => normalise(name) === 'finish') ?? -1;
      const gangIndex = product?.options.findIndex(name => normalise(name) === 'gangs') ?? -1;
      const functionName = control ? this.functionOption(control.style)?.name : null;
      const functionIndex = functionName
        ? product.options.findIndex(name => normalise(name) === functionName) : -1;

      return values.map(v => {
        let media = '';
        if (isFinish) {
          const candidates = finishIndex < 0 ? [] : product.variants.filter(variant =>
            normalise(variant.options[finishIndex]) === normalise(v) && variant.image
          );
          const sameGang = candidates.filter(variant =>
            gangIndex < 0 || normalise(variant.options[gangIndex]) === this.gang + 'g'
          );
          const imageVariant = sameGang.find(variant =>
            functionIndex < 0 || normalise(variant.options[functionIndex]) === normalise(control.function)
          ) || sameGang[0] || candidates[0];
          media = imageVariant
            ? `<span class="rb-finish-image"><img src="${escape(imageVariant.image)}" alt="" loading="lazy"></span>`
            : `<span class="rb-finish-image"><span class="rb-finish-swatch" style="background:${tones[v] || '#eee'}"></span></span>`;
        }

        return `<button type="button" class="rb-choice${isFinish ? ' rb-finish-choice' : ''}" data-choice="${choice}" data-value="${escape(v)}" ${index === undefined ? '' : `data-index="${index}"`} aria-pressed="${String(selected) === v}">
          <span class="rb-choice-check" aria-hidden="true">✓</span>
          ${media}<span class="rb-choice-label">${escape(labels[v] || v + 'G')}</span>
        </button>`;
      }).join('');
    }
    resolveVariant(product, selections) {
      if (!product) return null;
      const indexes = Object.keys(selections).map(name => product.options.findIndex(o => normalise(o) === name));
      if (indexes.some(i => i < 0)) return null;
      return product.variants.find(v => indexes.every((index, i) => normalise(v.options[index]) === normalise(Object.values(selections)[i]))) || null;
    }
    functionOption(style) {
  const product = this.data.products[style];
  if (!product || product.options.length !== 3) return null;
  if (!product.options.some(name => normalise(name) === 'finish')) return null;
  if (!product.options.some(name => normalise(name) === 'gangs')) return null;

  const index = product.options.findIndex(name => !['finish', 'gangs'].includes(normalise(name)));
  if (index < 0) return null;

  return {
    name: normalise(product.options[index]),
    values: [...new Set(product.variants.map(variant => variant.options[index]))]
  };
}

syncFunctions() {
  this.controls.forEach(control => {
    const option = this.functionOption(control.style);
    const values = option?.values || [];

    if (!values.includes(control.function)) {
      control.function = values[0] || '';
    }
  });
}

functionChoices(control, index) {
  const option = this.functionOption(control.style);
  if (!option) return '';

  const escape = value => String(value).replace(/[&<>"']/g, character => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  }[character]));

  return option.values.map(value => `
    <button
      type="button"
      class="rb-choice"
      data-choice="function"
      data-value="${escape(value)}"
      data-index="${index}"
      aria-pressed="${control.function === value}"
    ><span class="rb-choice-check" aria-hidden="true">✓</span><span class="rb-choice-label">${escape(value)}</span></button>
  `).join('');
}

parts() {
  const controlParts = this.controls.slice(0, this.gang).map((control, index) => {
    const option = this.functionOption(control.style);

    return {
      position: 'Position ' + (index + 1),
      variant: option ? this.resolveVariant(this.data.products[control.style], {
        finish: control.finish,
        gangs: this.gang + 'G',
        [option.name]: control.function
      }) : null
    };
  });

  let adjustment = 0;

  if (this.gang === 3 && controlParts.every(part => part.variant)) {
    const remainder = controlParts.reduce((sum, part) => {
      const price = part.variant.price;
      return sum + Math.round(price * 3 / 100) * 100 - price * 3;
    }, 0);

    adjustment = Math.round(remainder / 3);
  }

  const plateVariant = this.resolveVariant(this.data.products.plate, {
    gangs: this.gang + 'G',
    finish: this.finish,
    'price adjustment': String(adjustment)
  });

  return [
    {
      position: 'Plate',
      variant: plateVariant && plateVariant.price === adjustment ? plateVariant : null
    },
    ...controlParts
  ];
}
    summary() {
  return `${this.gang}G ${labels[this.finish]} plate; ` +
    this.controls.slice(0, this.gang).map((control, index) =>
      `${index + 1}: ${labels[control.style]} / ${labels[control.finish]} / ${control.function}`
    ).join('; ');
}
    buildItems(quantity, group) {
  const summary = this.summary();
  const layout = this.data.layout[String(this.gang)];

  return this.parts().map((part, index) => {
    const properties = {
      '_Configuration': group,
      '_Assembly': summary,
      'Component': part.position,
      '_repenic_configuration': group
    };

    let imageUrl = part.variant.image;

    if (index > 0) {
      const control = this.controls[index - 1];
      const product = this.data.products[control.style];
      const finishIndex = product.options.findIndex(name => normalise(name) === 'finish');
      const sharedImageVariant = product.variants.find(variant =>
        normalise(variant.options[finishIndex]) === normalise(control.finish) && variant.image
      );

      imageUrl = imageUrl || sharedImageVariant?.image;

      properties._rb_left = String(
        this.gang === 1
          ? (layout.left + layout.right) / 2
          : layout.left + (layout.right - layout.left) * (index - 1) / (this.gang - 1)
      );
      properties._rb_top = String(layout.top);
      properties._rb_width = String(layout.width * this.data.scale[control.style] / 100);
    }

    if (imageUrl) properties._rb_image = imageUrl;

    return {
      id: part.variant.id,
      quantity,
      properties
    };
  });
}
    render() {
  this.syncFunctions();
      this.querySelector('[data-rb-gangs]').innerHTML = this.choices(['1', '2', '3', '4'], this.gang, 'gang');
      this.querySelector('[data-rb-finishes]').innerHTML = this.choices(finishes, this.finish, 'plate');
      const positionsContainer = this.querySelector('[data-rb-positions]');
      const openPositions = new Set(
        Array.from(positionsContainer.querySelectorAll('.rb-position')).flatMap((element, index) =>
          element.open ? [index] : []
        )
      );

      positionsContainer.innerHTML = this.controls.slice(0, this.gang).map((c, i) => `
        <details class="rb-position"${openPositions.has(i) ? ' open' : ''}>
          <summary>
  <span class="rb-summary-thumbnail" data-rb-position-thumbnail="${i}"></span>
  <span class="rb-summary-title">
    <span class="rb-summary-label">Position ${i + 1}</span>
    <span class="rb-summary-value" data-rb-control-summary="${i}"></span>
  </span>
</summary>
          <fieldset class="rb-style-fieldset">
            <legend class="visually-hidden">Control style</legend>
            <div class="rb-style-cards">
              ${styles.map(style => `
                <button
                  type="button"
                  class="rb-style-card"
                  data-choice="style"
                  data-value="${style}"
                  data-index="${i}"
                  aria-pressed="${c.style === style}"
                >
                  <span class="rb-style-image" data-rb-style-image="${style}">
                    <span class="rb-style-check" aria-hidden="true">✓</span>
                  </span>
                  <span class="rb-style-label">${labels[style]}</span>
                </button>
              `).join('')}
            </div>
          </fieldset>
          <fieldset class="rb-function-fieldset">
            <legend>${c.style === 'rotary' ? 'Dimmer type' : 'Toggle switch type'}</legend>
            <div class="rb-function-list">${this.functionChoices(c, i)}</div>
          </fieldset>
          <fieldset>
            <legend>Control finish</legend>
            <div class="rb-choices">${this.choices(finishes, c.finish, 'finish', i)}</div>
          </fieldset>
        </details>
      `).join('');
            const selectedParts = this.parts();
      const plateWidth = this.gang <= 2 ? 86 : 146;
      const dimensions = this.querySelector('[data-rb-dimensions]');
      if (dimensions) dimensions.textContent = 'Width ' + plateWidth + ' mm × Height 86 mm';

      const gangSummary = this.querySelector('[data-rb-gang-summary]');
      if (gangSummary) gangSummary.textContent = this.gang + 'G';

      const finishSummary = this.querySelector('[data-rb-finish-summary]');
      if (finishSummary) finishSummary.textContent = labels[this.finish] || this.finish;

      positionsContainer.querySelectorAll('[data-rb-control-summary]').forEach(element => {
        const control = this.controls[Number(element.dataset.rbControlSummary)];
        element.textContent = [
          labels[control.style] || control.style,
          control.function,
          labels[control.finish] || control.finish
        ].filter(Boolean).join(' · ');
      });
      const plateThumbnailUrl = selectedParts[0].variant?.image;
      this.querySelectorAll('[data-rb-plate-thumbnail]').forEach(image => {
        if (plateThumbnailUrl) {
          image.src = plateThumbnailUrl;
          image.hidden = false;
        } else {
          image.removeAttribute('src');
          image.hidden = true;
        }
      });
      positionsContainer.querySelectorAll('[data-rb-position-thumbnail]').forEach(container => {
        const index = Number(container.dataset.rbPositionThumbnail);
        const control = this.controls[index];
        const product = this.data.products[control.style];
        const finishIndex = product?.options.findIndex(name => normalise(name) === 'finish') ?? -1;
        const sharedImageVariant = finishIndex < 0 ? null : product.variants.find(variant =>
          normalise(variant.options[finishIndex]) === normalise(control.finish) && variant.image
        );
        const url = selectedParts[index + 1].variant?.image || sharedImageVariant?.image;
        if (!url) return;
        const image = document.createElement('img');
        image.src = url;
        image.alt = '';
        image.loading = 'lazy';
        container.append(image);
      });

positionsContainer.querySelectorAll('[data-rb-style-image]').forEach(container => {
  const url = this.data.thumbnails?.[container.dataset.rbStyleImage];
  if (!url) return;

  const image = document.createElement('img');
  image.src = url;
  image.alt = '';
  image.loading = 'lazy';
  container.prepend(image);
});

const parts = this.parts();
const missing = parts.find(p => !p.variant);
      const soldOut = parts.find(p => p.variant && !p.variant.available);
      const money = amount => new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: this.data.currency }).format(amount / 100);
      this.querySelector('[data-rb-price]').textContent = missing ? 'Price unavailable' : money(parts.reduce((sum, p) => sum + p.variant.price, 0));
      this.querySelector('[data-rb-add]').disabled = !!missing || !!soldOut || this.busy;
      this.querySelector('[data-rb-status]').textContent = missing ? `${missing.position} is not configured. Check the selected products and their Gangs / Finish options.` : soldOut ? `${soldOut.position} is sold out in this finish.` : '';
      this.renderPreview(parts);
    }
    renderPreview(parts) {
      const canvas = this.querySelector('[data-rb-canvas]');
      canvas.replaceChildren();
      let imagesComplete = true;
      const imageUrl = v => v?.image || null;
      const plateUrl = imageUrl(parts[0].variant);
      if (plateUrl) {
        const image = document.createElement('img'); image.src = plateUrl; image.className = 'rb-plate'; image.alt = `${this.gang}G ${labels[this.finish]} plate`; canvas.append(image);
      } else {
        imagesComplete = false;
        const plate = document.createElement('div'); plate.className = 'rb-placeholder-plate'; plate.style.background = tones[this.finish]; canvas.append(plate);
      }
      const layout = this.data.layout[String(this.gang)];
      this.controls.slice(0, this.gang).forEach((control, i) => {
        const product = this.data.products[control.style];
const finishIndex = product?.options.findIndex(name => normalise(name) === 'finish') ?? -1;
const sharedImageVariant = finishIndex < 0 ? null : product.variants.find(variant =>
  normalise(variant.options[finishIndex]) === normalise(control.finish) && variant.image
);
const url = imageUrl(parts[i + 1].variant) || imageUrl(sharedImageVariant);
        let element;
        if (url) { element = document.createElement('img'); element.src = url; element.className = 'rb-control'; element.alt = `Position ${i + 1}: ${labels[control.finish]} ${labels[control.style]}`; }
        else { imagesComplete = false; element = document.createElement('div'); element.className = 'rb-placeholder-control'; element.dataset.style = control.style; element.style.background = tones[control.finish]; element.setAttribute('aria-label', `Position ${i + 1}: ${labels[control.finish]} ${labels[control.style]}`); }
        element.style.left = (this.gang === 1 ? (layout.left + layout.right) / 2 : layout.left + (layout.right - layout.left) * i / (this.gang - 1)) + '%';
        element.style.top = layout.top + '%';
        element.style.width = (layout.width * this.data.scale[control.style] / 100) + '%';
        canvas.append(element);
      });
      this.querySelector('[data-rb-preview-note]').textContent = imagesComplete ? '' : 'Illustrative preview. Product images will appear once assigned to the selected variants.';
    }
    async request(path, options) {
      const response = await fetch((window.Shopify?.routes?.root || '/') + path, options);
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Unable to update the cart.');
      return data;
    }
    async rollback(group) {
      for (let attempt = 0; attempt <= 2; attempt++) {
        const cart = await this.request('cart.js', {
          cache: 'no-store'
        });

        const members = cart.items.filter(
          item => item.properties?._repenic_configuration === group
        );

        if (!members.length) return;
        if (attempt === 2) break;

        const updates = Object.fromEntries(
          members.map(item => [item.key, 0])
        );

        try {
          await this.request('cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates })
          });
        } catch (_) {
          // 请求失败也可能已执行，重新读取购物车确认。
        }
      }

      throw new Error('The configuration could not be completely removed.');
    }
    async addToCart() {
      if (this.busy || this.parts().some(p => !p.variant?.available)) return;
      const quantity = Number(this.querySelector('[data-rb-quantity]').value);
      const status = this.querySelector('[data-rb-status]');
      if (!Number.isSafeInteger(quantity) || quantity < 1) { status.textContent = 'Enter a whole quantity of at least 1.'; return; }
      const group = 'RP-' + (globalThis.crypto?.randomUUID?.() || Date.now().toString(36) + Math.random().toString(36).slice(2));
      const items = this.buildItems(quantity, group);
      this.busy = true;
      this.querySelectorAll('button, input').forEach(element => element.disabled = true);
      const add = this.querySelector('[data-rb-add]');
      window.clearTimeout(this.addFeedbackTimer);
      add.setAttribute('aria-busy', 'true');
      add.textContent = 'Adding…';
      status.textContent = '';
      let posted = false;
      try {
        // Preflight checks total demand per variant, including repeated positions and existing cart quantities.
        const cart = await this.request('cart.js');
        const demand = new Map();
        items.forEach(item => demand.set(item.id, (demand.get(item.id) || 0) + item.quantity));
        for (const [id, amount] of demand) {
          const variant = this.parts().find(p => p.variant.id === id).variant;
          const existing = cart.items.filter(i => i.variant_id === id).reduce((sum, i) => sum + i.quantity, 0);
          if (variant.managed && variant.policy === 'deny' && existing + amount > variant.stock) throw new Error('Insufficient stock for the selected quantity or repeated controls.');
          const rule = variant.rule;
          if (rule && (amount < rule.min || amount % rule.increment !== 0 || (rule.max !== null && rule.max !== undefined && existing + amount > rule.max))) throw new Error('The selected components have quantity limits. Please adjust the quantity.');
        }
        posted = true;
        await this.request('cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) });
        const updated = await this.request('cart.js');
        const added = updated.items.filter(i => i.properties?._repenic_configuration === group);
        if (items.some(item => added.filter(i => i.variant_id === item.id && i.properties?.Component === item.properties.Component).reduce((sum, i) => sum + i.quantity, 0) !== quantity)) throw new Error('The complete configuration could not be added.');
        try {
  const drawer = document.querySelector('cart-drawer-component');

  if (!drawer) {
    window.location.assign((window.Shopify?.routes?.root || '/') + 'cart');
    return;
  }

  const { CartUpdateEvent } = await import('@theme/events');
  const { morphSection } = await import('@theme/section-renderer');

  const sectionIds = [...new Set(
    Array.from(document.querySelectorAll('cart-items-component'))
      .map(component => component.dataset.sectionId)
      .filter(Boolean)
  )].slice(0, 5);

  if (!sectionIds.length) {
    throw new Error('Cart section not found.');
  }

  const refreshedCart = await this.request('cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      updates: {},
      sections: sectionIds,
      sections_url: window.location.pathname
    })
  });

  if (sectionIds.some(id => !refreshedCart.sections?.[id])) {
    throw new Error('Cart could not be refreshed.');
  }

  for (const sectionId of sectionIds) {
    await morphSection(sectionId, refreshedCart.sections[sectionId], {
      mode: 'hydration'
    });
  }

  this.dispatchEvent(new CartUpdateEvent(refreshedCart, 'repenic-builder', {
    itemCount: refreshedCart.item_count,
    source: 'repenic-builder',
    sections: refreshedCart.sections
  }));

  this.busy = false;
  this.querySelectorAll('button, input').forEach(element => {
    element.disabled = false;
  });
  this.render();
  add.removeAttribute('aria-busy');
  add.textContent = 'Added to Basket';
  this.addFeedbackTimer = window.setTimeout(() => {
    if (!this.busy && add.isConnected) {
      add.textContent = 'Add to basket';
    }
  }, 1200);

  this.dialog.close();
  this.unlock();

  await customElements.whenDefined('cart-drawer-component');
  const currentDrawer = document.querySelector('cart-drawer-component');
  if (!currentDrawer) throw new Error('Cart drawer not found.');

  currentDrawer.open();
} catch (drawerError) {
  window.location.assign((window.Shopify?.routes?.root || '/') + 'cart');
}
      } catch (error) {
        let cleanupFailed = false;

        if (posted) {
          try {
            await this.rollback(group);
          } catch (_) {
            cleanupFailed = true;
          }
        }

        if (cleanupFailed) {
          // 保持组合器锁定，避免重复加购。
          this.busy = true;
          document.documentElement.classList.add('rb-cart-updating');

          add.textContent = 'Checking cart…';
          status.textContent =
            'Unable to confirm your cart. Opening the cart to check it. If it does not load, reconnect and reload before checkout.';

          // 跳转完成前，阻止当前页面继续结账。
          const blockCheckout = event => {
            const target = event.target;
            if (!(target instanceof Element)) return;

            const checkout = target.closest(
              '[name="checkout"], .cart__checkout-button, a[href*="/checkout"], shopify-accelerated-checkout-cart'
            );

            const cartSubmit = event.type === 'submit' && (
              event.submitter?.name === 'checkout' ||
              /\/(?:cart|checkout)(?:[/?#]|$)/.test(
                target.getAttribute('action') || ''
              )
            );

            if (checkout || cartSubmit) {
              event.preventDefault();
              event.stopImmediatePropagation();
            }
          };

          document.addEventListener('click', blockCheckout, true);
          document.addEventListener('submit', blockCheckout, true);

          window.location.assign(
            (window.Shopify?.routes?.root || '/') + 'cart'
          );
          return;
        }

        this.busy = false;
        this.querySelectorAll('button, input').forEach(
          element => element.disabled = false
        );
        this.render();
        add.removeAttribute('aria-busy');
        add.textContent = 'Try Again';
        status.textContent =
          error.message || 'Unable to add this configuration. Please try again.';
      }
    }
  }
  customElements.define('repenic-builder', RepenicBuilder);
})();
